<template>
  <div class="app-container">
    <div class="app-content switch-home">
      <!-- 顶部工具栏：查询 + 添加 -->
      <div class="switch-home__toolbar">
        <div class="switch-home__toolbar-search">
          <span class="switch-home__search-label">采集器编号</span>
          <el-input
            v-model.trim="queryForm.gatewaySn"
            class="switch-home__search-input"
            placeholder="请输入采集器编号"
            clearable
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-button
            type="primary"
            icon="el-icon-search"
            style="margin-left: 10px;"
            :loading="loading"
            @click="handleSearch"
          >查询</el-button>
        </div>

        <div class="switch-home__toolbar-action">
          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="openAddDialog"
          >添加开关</el-button>
        </div>
      </div>

      <!-- 开关组件列表 -->
      <div
        v-loading="loading"
        class="switch-home__content"
        element-loading-text="获取开关列表"
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(0, 0, 0, 0.6)"
      >
        <div v-if="switchList.length" class="switch-home__grid">
          <template v-for="item in switchList">
            <singleSwitch
              v-if="item.deviceType === 'single'"
              :key="`single_${item.id}`"
              :ref="`singleSwitch_${item.id}`"
              :switch-info="item"
              @openEditDialog="handleOpenEdit"
              @deleteSwitch="handleDeleteSwitch"
              @operateSwitchValidate="handleOperateSwitchValidate"
            />
            <doubleSwitch
              v-else
              :key="`double_${item.id}`"
              :ref="`doubleSwitch_${item.id}`"
              :switch-info="item"
              @deleteSwitch="handleDeleteSwitch"
              @operateSwitchValidate="handleOperateSwitchValidate"
            />
          </template>
        </div>

        <!-- 空数据 -->
        <div v-else class="switch-home__empty">
          <i class="switch-home__empty-icon el-icon-box" />
          <p>暂无数据</p>
        </div>
      </div>

      <!-- 分页 -->
      <div class="switch-home__pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page.sync="pageConfig.page"
          :page-size="pageConfig.perPage"
          :total="pageConfig.totalCount"
        />
      </div>

      <!-- 添加开关弹框 -->
      <el-dialog
        title="添加开关"
        width="460px"
        append-to-body
        :close-on-click-modal="false"
        :visible.sync="addDialogVisible"
      >
        <template #default>
          <el-form
            ref="addFormRef"
            :model="addForm"
            :rules="addFormRules"
            label-width="100px"
          >
            <el-form-item label="采集器编号" prop="gatewaySn">
              <el-input
                v-model.trim="addForm.gatewaySn"
                placeholder="请输入数据采集器编号"
                clearable
              />
            </el-form-item>
            <el-form-item label="开关名称" prop="switchAlias">
              <el-input
                v-model.trim="addForm.switchAlias"
                placeholder="请输入开关名称"
                clearable
                maxlength="30"
                show-word-limit
              />
            </el-form-item>
          </el-form>
        </template>
        <template #footer>
          <el-button @click="addDialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="submitIng" @click="handleSubmitAdd">保 存</el-button>
        </template>
      </el-dialog>

      <!-- 编辑开关弹框 -->
      <el-dialog
        title="编辑开关"
        width="460px"
        append-to-body
        :close-on-click-modal="false"
        :visible.sync="editDialogVisible"
      >
        <template #default>
          <el-form
            ref="editFormRef"
            :model="editForm"
            :rules="editFormRules"
            label-width="100px"
          >
            <el-form-item label="开关名称" prop="alias">
              <el-input
                v-model.trim="editForm.alias"
                placeholder="请输入开关名称"
                clearable
                maxlength="30"
                show-word-limit
              />
            </el-form-item>
            <template v-if="editForm.deviceType === 'double'">
              <el-form-item
                v-for="(groupName, index) in editForm.groupNames"
                :key="index"
                :label="`${index + 1}组名称`"
                :prop="`groupNames.${index}`"
                :rules="[
                  { required: true, message: `请输入${index + 1}组名称`, trigger: 'blur' }
                ]"
              >
                <el-input
                  v-model.trim="editForm.groupNames[index]"
                  :placeholder="`请输入${index + 1}组名称`"
                  clearable
                  maxlength="20"
                  show-word-limit
                />
              </el-form-item>
            </template>
            <el-form-item label="采集器编号">
              <el-input :value="editForm.gatewaySn" disabled />
            </el-form-item>
          </el-form>
        </template>
        <template #footer>
          <el-button @click="editDialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="submitIng" @click="handleSubmitEdit">保 存</el-button>
        </template>
      </el-dialog>

      <!-- 操作码校验弹框 -->
      <operate-dialog
        :visible.sync="operateDialog.visible"
        :switch-alias="operateDialog.alias"
        :operate-type="operateDialog.operateType"
        :org-id="operateDialog.orgId"
        :switch-device-id="operateDialog.switchDeviceId"
        @confirm="handleOperateConfirm"
        @cancel="handleOperateCancel"
      />
    </div>
  </div>
</template>

<script>
import { PageQuerySwitch, UpdateSingleSwitchAlias, AddSwitch, DeleteSwitch } from '@/api/SwitchDevice'
import operateDialog from './component/operateDialog.vue'
import singleSwitch from './component/singleSwitch.vue'
import doubleSwitch from './component/doubleSwitch.vue'

/** 开关状态字典 */
const STATUS_DICT = {
  /** 合闸 */
  close: { label: '合闸' },
  /** 分闸 */
  open: { label: '分闸' },
  /** 离线 */
  offline: { label: '离线' }
}
/** 状态池 */
const STATUS_POOL = ['close', 'open', 'offline']

export default {
  name: 'SwitchNewHome',
  components: {
    operateDialog,
    singleSwitch,
    doubleSwitch
  },
  data () {
    return {
      /** 开关列表（演示数据） */
      switchList: [],
      /** 查询条件 */
      queryForm: {
        /** 电站ID */
        Orgid: '',
        /** 采集器编号 */
        gatewaySn: ''
      },
      pageConfig: {
        /** 页大小 */
        perPage: 8,
        /** 页码 */
        page: 1,
        /** 排序字段 */
        orderBy: 'createtime desc',
        /** 数据总数 */
        totalCount: 0,
        /** 过滤条件 */
        whereCondition: ''
      },
      /** 加载状态 */
      loading: false,
      /** 添加弹框 */
      addDialogVisible: false,
      addForm: {
        orgId: '',
        gatewaySn: '',
        switchAlias: '',
        deviceType: 'io2'
      },
      addFormRules: {
        gatewaySn: [
          { required: true, message: '请输入数据采集器编号', trigger: 'blur' }
        ],
        switchAlias: [
          { required: true, message: '请输入开关名称', trigger: 'blur' }
        ]
      },
      /** 编辑弹框 */
      editDialogVisible: false,
      editForm: {
        deviceId: '',
        alias: '',
        gatewaySn: '',
        deviceType: '',
        groupNames: []
      },
      editFormRules: {
        alias: [
          { required: true, message: '请输入开关名称', trigger: 'blur' }
        ]
      },
      /** 提交中状态 */
      submitIng: false,
      /** 操作码校验弹框 */
      operateDialog: {
        visible: false,
        alias: '',
        operateType: 'open',
        currentItem: null,
        currentGroup: null,
        orgId: '',
        switchDeviceId: ''
      }
    }
  },
  created () {
    this.queryForm.Orgid = this.$store.getters.station.id
    this.addForm.orgId = this.$store.getters.station.id
    this.operateDialog.orgId = this.$store.getters.station.id
  },
  mounted () {
    this.getSwitchPage()
  },
  methods: {
    /** 状态文案 */
    statusText (status) {
      const dict = STATUS_DICT[status]
      return dict ? dict.label : status
    },

    /** 组装操作对象的展示名称 */
    displayName (item, group) {
      return item.deviceType === 'double'
        ? `${item.alias} · ${group.name}`
        : item.alias
    },

    /** 分页获取开关列表 */
    getSwitchPage () {
      this.switchList = []
      this.loading = true
      this.pageConfig.whereCondition = JSON.stringify(this.queryForm)
      PageQuerySwitch(this.pageConfig).then(({ data }) => {
        const { queryResults, TotalCount } = data
        this.pageConfig.totalCount = TotalCount
        if (queryResults && queryResults.length > 0) {
          this.switchList = queryResults.map(item => {
            const resultItem = {
              id: item.deviceid,
              deviceType: item.devicemodel === 'io2' ? 'single' : 'double',
              gatewaySn: item.gatewaysn,
              alias: item.devicealias,
              groups: []
            }
            if (resultItem['deviceType'] === 'single') {
              resultItem.groups.push({
                id: item.deviceid,
                name: item.devicealias,
                address: '01',
                status: STATUS_POOL[2]
              })
            } else {
              resultItem.groups.push({
                id: item.deviceid,
                name: item.switchname1,
                address: '01',
                status: STATUS_POOL[2]
              })
              resultItem.groups.push({
                id: item.deviceid,
                name: item.switchname2,
                address: '02',
                status: STATUS_POOL[2]
              })
            }
            return resultItem
          })
        }
      }).then(() => {
        this.loading = false
      }).catch(() => {
        this.loading = false
        this.$message({
          message: '获取开关列表失败',
          type: 'error'
        })
      })
    },

    /** 查询 */
    handleSearch () {
      this.loading = true
      this.pageConfig.page = 1
      this.getSwitchPage()
    },

    /** 打开添加弹框 */
    openAddDialog () {
      this.addDialogVisible = true
      this.$nextTick(() => {
        this.$refs.addFormRef && this.$refs.addFormRef.clearValidate()
      })
    },
    /** 提交添加（演示：不请求接口，直接加入列表） */
    handleSubmitAdd () {
      this.$refs.addFormRef.validate(validResult => {
        if (validResult) {
          this.submitIng = true
          const postData = {
            orgId: this.addForm.orgId,
            gatewaySn: this.addForm.gatewaySn,
            switchAlias: this.addForm.switchAlias
          }
          AddSwitch(postData).then(() => {
            this.$message({
              message: '添加成功',
              type: 'success'
            })
            this.addDialogVisible = false
            this.submitIng = false
            this.getSwitchPage()
          }).catch(() => {
            this.submitIng = false
            this.$message({
              message: '添加失败',
              type: 'error'
            })
          })
        }
      })
    },

    /** 打开编辑弹框 */
    handleOpenEdit (item) {
      this.editForm.deviceId = item.id
      this.editForm.alias = item.alias
      this.editForm.gatewaySn = item.gatewaySn
      this.editForm.deviceType = item.deviceType
      this.editForm.groupNames = item.groups.map(group => group.name)
      this.editDialogVisible = true
      console.log(item)
    },
    /** 提交编辑别名/组名 */
    handleSubmitEdit () {
      this.$refs.editFormRef.validate(validResult => {
        if (validResult) {
          this.submitIng = true
          const postData = {
            deviceId: this.editForm.deviceId,
            alias: this.editForm.alias
          }
          UpdateSingleSwitchAlias(postData).then(() => {
            this.$msgSuccess('名称修改成功')
            this.switchList.forEach(target => {
              if (target.id === postData.deviceId) {
                target.alias = postData.alias
              }
            })
            this.editDialogVisible = false
            this.submitIng = false
          }).catch(() => {
            this.$msgError('名称修改失败')
            this.submitIng = false
          })
        }
      })
    },

    /** 删除开关 */
    handleDeleteSwitch (item) {
      this.$confirm(
        `确定删除开关「${item.alias}」吗？删除后将无法恢复。`,
        '删除确认',
        {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.submitIng = true
        DeleteSwitch({ deviceId: item.id }).then(() => {
          this.$message({
            message: '删除成功',
            type: 'success'
          })
          this.submitIng = false
          this.getSwitchPage()
        }).catch(() => {
          this.submitIng = false
          this.$message({
            message: '删除失败',
            type: 'error'
          })
        })
      }).catch(() => {})
    },

    /**
     * 执行某一组开关操作前的状态预检
     * @param item 开关对象
     * @param group 组对象（单组开关即唯一组）
     * @param operateType 1：分闸, 2：合闸
     */
    handleOperateSwitchValidate (item, groupIndex, operateType) {
      // 离线状态不允许操作，提示刷新
      const group = item.groups[groupIndex]
      this.operateDialog.alias = group.name
      this.operateDialog.operateType = operateType
      this.operateDialog.currentItem = item
      this.operateDialog.currentGroup = group
      this.operateDialog.switchDeviceId = item.id
      this.operateDialog.visible = true
    },
    /** 取消操作 */
    handleOperateCancel () {
      this.operateDialog.visible = false
      this.operateDialog.alias = ''
      this.operateDialog.operateType = 0
      this.operateDialog.currentItem = null
      this.operateDialog.currentGroup = null
      this.operateDialog.switchDeviceId = null
    },

    /** 操作码校验通过后执行操作（演示：本地切换该组状态） */
    handleOperateConfirm () {
      this.operateDialog.visible = false
      const { switchDeviceId } = this.operateDialog
      if (!switchDeviceId) {
        return
      }
      const switchInfo = this.switchList.find(item => item.id === switchDeviceId)
      if (!switchInfo) {
        return
      }
      const refPrefix = switchInfo.deviceType === 'single' ? 'singleSwitch_' : 'doubleSwitch_'
      const switchRef = `${refPrefix}${switchInfo.id}`
      const switchComponent = this.$refs[switchRef][0]
      console.log(switchComponent)
      switchComponent.handleOperateConfirmCall().then(() => {
        this.operateDialog.visible = false
        this.operateDialog.alias = ''
        this.operateDialog.operateType = 0
        this.operateDialog.currentItem = null
        this.operateDialog.currentGroup = null
        this.operateDialog.switchDeviceId = null
      })
    }
  }
}
</script>

<style scoped>
@import './index.css';
</style>
